/**
 * ShopPage (Магазин) — the games → categories → products browse flow (ТЗ §3, §5).
 *
 * The selected game/category are encoded in the route (``/shop/:gameId/:categoryId``)
 * so navigation and the Telegram BackButton compose naturally. Search + sort are
 * client-held intent forwarded to the server (the frontend never filters locally).
 *
 * Layout follows the approved DESIGN frames 02 (games) / 03 (categories) / 04
 * (products). The Telegram BackButton and the app shell's bottom nav are intentionally
 * not redrawn here.
 */
import { useBackButton } from "@melusine/shared-telegram";
import { useCategories } from "@entities/category";
import { useGames, type Game } from "@entities/game";
import { useMe } from "@entities/user";
import type { ProductListItem } from "@entities/product";
import {
  CatalogFilterBar,
  useCatalogFilter,
} from "@features/catalog-filter";
import { useCart, useCartQuote, cartCount } from "@features/cart";
import {
  CategoryChips,
  GameGrid,
  GameHeaderCard,
  ProductGrid,
} from "@widgets/catalog-browser";
import { CartBar } from "@widgets/cart-bar";
import { ShopClosedBanner, ShopHours } from "@widgets/shop-status";
import { ApiError } from "@shared/api";
import { routes, useNavigate } from "@shared/router";

import styles from "./ShopPage.module.css";

function apiErrorText(err: ApiError, fallback: string): string {
  const payload = err.payload as { readonly detail?: unknown } | null;
  if (payload !== null && typeof payload.detail === "string") {
    return payload.detail;
  }
  return fallback;
}

export interface ShopPageProps {
  /** Active game (from the route); ``undefined`` shows the game picker. */
  readonly gameId?: number;
  /** Active category narrowing the listing. */
  readonly categoryId?: number;
}

const SearchIcon = (): JSX.Element => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

export function ShopPage({ gameId, categoryId }: ShopPageProps): JSX.Element {
  const navigate = useNavigate();
  const filter = useCatalogFilter();
  const { data: me } = useMe();
  const { data: games } = useGames();
  const { data: categories } = useCategories(gameId);

  // ── local cart (ТЗ §8-10), active only inside a category's product listing ──
  const cart = useCart();
  const quoteState = useCartQuote(cart.items);
  const selectedCount = cartCount(cart.items);

  // «Оформить заказ» no longer creates the order — it opens the confirmation screen (ТЗ §5).
  const runCheckout = (): void => {
    navigate(routes.checkout());
  };

  const cartError =
    quoteState.error !== null
      ? apiErrorText(quoteState.error, "Не удалось рассчитать сумму.")
      : null;

  // Cart wiring shared by every product listing (game-level grid + category grid), so the
  // "+"/stepper and the checkout bar are reachable as soon as products are on screen.
  const cartProps = {
    qtyOf: cart.qtyOf,
    onAdd: cart.add,
    onIncrement: cart.increment,
    onDecrement: cart.decrement,
  };
  const cartBar =
    selectedCount > 0 ? (
      <>
        {/* Spacer so the last card isn't hidden behind the fixed cart bar. */}
        <div className={styles.cartSpacer} aria-hidden="true" />
        <CartBar
          count={selectedCount}
          totalRub={quoteState.quote?.total_rub ?? null}
          isQuoting={quoteState.isLoading}
          isSubmitting={false}
          error={cartError}
          onCancel={cart.clear}
          onCheckout={runCheckout}
        />
      </>
    ) : null;

  // Back goes to the game picker when inside a game, else to Home.
  useBackButton(() => {
    if (gameId !== undefined) {
      navigate(routes.shop());
    } else {
      navigate(routes.home());
    }
  });

  const selectGame = (game: Game): void => {
    navigate(routes.shopGame(game.id));
  };
  const selectCategory = (nextCategoryId: number | undefined): void => {
    if (gameId === undefined) {
      return;
    }
    navigate(routes.shopGame(gameId, nextCategoryId));
  };
  const openProduct = (product: ProductListItem): void => {
    navigate(routes.product(product.id));
  };

  // Working-hours surface (ТЗ №7): schedule line on the picker, soft closed banner everywhere.
  const closedBanner = me ? <ShopClosedBanner me={me} /> : null;

  // ---- Frame 02 · game picker ----
  if (gameId === undefined) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>Магазин</h1>
        </header>
        {me ? <ShopHours me={me} /> : null}
        {closedBanner}
        <label className={styles.searchPill}>
          <span className={styles.searchIcon}>
            <SearchIcon />
          </span>
          <input
            type="search"
            inputMode="search"
            className={styles.searchInput}
            placeholder="Поиск по играм и услугам"
            aria-label="Поиск по играм и услугам"
            value={filter.search}
            onChange={(event) => {
              filter.setSearch(event.target.value);
            }}
          />
        </label>
        <p className={styles.sectionLabel}>Выберите игру</p>
        <GameGrid onSelect={selectGame} />
      </div>
    );
  }

  const activeGame = games?.find((game) => game.id === gameId);
  const activeCategory =
    categoryId !== undefined
      ? categories?.find((category) => category.id === categoryId)
      : undefined;

  const params = filter.toParams({
    game: gameId,
    ...(categoryId !== undefined ? { category: categoryId } : {}),
  });

  // ---- products within a category (donate-shop grid + local cart) ----
  if (categoryId !== undefined) {
    return (
      <div className={styles.page}>
        <GameHeaderCard
          game={activeGame}
          fallbackTitle={activeCategory?.name ?? "Услуги"}
          subtitle={activeCategory?.name ?? "Каталог услуг"}
        />
        {closedBanner}
        <CatalogFilterBar filter={filter} />
        <ProductGrid params={params} onSelect={openProduct} cart={cartProps} />
        {cartBar}
      </div>
    );
  }

  // ---- category picker for a game (header + grid of all services) ----
  return (
    <div className={styles.page}>
      <GameHeaderCard
        game={activeGame}
        fallbackTitle="Магазин"
        subtitle="Выберите категорию услуг"
      />
      {closedBanner}
      <CategoryChips
        gameId={gameId}
        selectedCategoryId={categoryId}
        onSelect={selectCategory}
      />
      <CatalogFilterBar filter={filter} />
      <ProductGrid params={params} onSelect={openProduct} cart={cartProps} />
      {cartBar}
    </div>
  );
}
